import type { HTMLAttributes, ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { joinCss } from '@/shared/lib/css-utils';
import css from './tooltip.module.scss';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

// `localsConvention: 'camelCaseOnly'` exposes the `.pos-*` SCSS classes as
// camelCase keys only, so a dynamic `css[`pos-${position}`]` lookup resolves
// to `undefined`. Map the position to the actual exported key.
const POSITION_CLASS: Record<TooltipPosition, string> = {
  top: 'posTop',
  bottom: 'posBottom',
  left: 'posLeft',
  right: 'posRight',
};

// Omit the native `content` attribute (typed `string`) so rich nodes —
// e.g. multi-line diagnostic lists — are accepted.
type TooltipProps = Omit<HTMLAttributes<HTMLDivElement>, 'content'> & {
  content: ReactNode;
  position?: TooltipPosition;
  // Extra class applied to the portal bubble — lets a consumer opt out of the
  // default 260px text cap for rich content (e.g. a data table) that should
  // size to its own content instead of wrapping.
  bubbleClassName?: string;
  // Rich content (e.g. a long description): wider bubble, capped height with
  // internal scroll, and a short hover-bridge so the pointer can reach it —
  // instead of an unbounded narrow-and-tall block. Plain tooltips are
  // unaffected (immediate hide, no pointer events on the bubble).
  rich?: boolean;
  // When true, the wrapper renders but never shows a bubble. Lets a consumer
  // keep the SAME DOM structure (so a measured child isn't reparented) while
  // toggling the tooltip on/off — e.g. show-only-when-truncated.
  disabled?: boolean;
};

const GAP_PX = 8;
// Keep the bubble at least this far from every viewport edge.
const VIEWPORT_MARGIN = 8;
// Grace period before a rich tooltip hides, so the pointer can cross the gap
// from the trigger onto the bubble to scroll it.
const RICH_HIDE_DELAY_MS = 120;

type Offset = { left: number; top: number; transform: string };

// Compute the fixed-position offset for the bubble based on the trigger's
// bounding rect + the requested position. Returns the top/left in CSS pixel
// space relative to the viewport — the bubble itself uses a transform to
// center along the axis the position doesn't pin.
const computeOffset = (rect: DOMRect, position: TooltipPosition): Offset => {
  switch (position) {
    case 'top':
      return {
        left: rect.left + rect.width / 2,
        top: rect.top - GAP_PX,
        transform: 'translate(-50%, -100%)',
      };
    case 'bottom':
      return {
        left: rect.left + rect.width / 2,
        top: rect.bottom + GAP_PX,
        transform: 'translate(-50%, 0)',
      };
    case 'left':
      return {
        left: rect.left - GAP_PX,
        top: rect.top + rect.height / 2,
        transform: 'translate(-100%, -50%)',
      };
    case 'right':
      return {
        left: rect.right + GAP_PX,
        top: rect.top + rect.height / 2,
        transform: 'translate(0, -50%)',
      };
    default:
      return {
        left: rect.left + rect.width / 2,
        top: rect.top - GAP_PX,
        transform: 'translate(-50%, -100%)',
      };
  }
};

export const Tooltip = ({
  content,
  position = 'top',
  className,
  bubbleClassName,
  rich = false,
  disabled = false,
  children,
  ...restProps
}: TooltipProps) => {
  const [visible, setVisible] = useState(false);
  const [offset, setOffset] = useState<Offset | null>(null);
  // Post-layout correction that pulls the bubble back inside the viewport.
  const [nudge, setNudge] = useState({ dx: 0, dy: 0 });
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const show = () => {
    clearHideTimer();
    setVisible(true);
  };
  const hide = () => {
    clearHideTimer();
    setVisible(false);
  };
  // Rich tooltips linger briefly so the pointer can move onto the bubble;
  // plain tooltips hide immediately (unchanged behaviour).
  const requestHide = () => {
    if (!rich) {
      hide();
      return;
    }
    clearHideTimer();
    hideTimer.current = setTimeout(() => setVisible(false), RICH_HIDE_DELAY_MS);
  };

  useEffect(() => clearHideTimer, []);

  // A tooltip only makes sense while hovering. Any other interaction —
  // scrolling (the bubble is position:fixed and would otherwise float
  // detached), the mouse wheel, or a click (e.g. opening a dropdown under the
  // trigger) — dismisses it. Rich tooltips are excluded when the event
  // originates inside the bubble so their own internal scroll still works.
  useEffect(() => {
    if (!visible) return undefined;
    const dismiss = (e: Event) => {
      if (rich && bubbleRef.current?.contains(e.target as Node)) return;
      setVisible(false);
    };
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('wheel', dismiss, { capture: true, passive: true });
    window.addEventListener('mousedown', dismiss, true);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('wheel', dismiss, true);
      window.removeEventListener('mousedown', dismiss, true);
    };
  }, [visible, rich]);

  // Re-measure the trigger's bounding rect on every show so the bubble
  // tracks layout shifts that happened while hidden. Portal-rendered
  // bubbles bypass ancestor `overflow: hidden` — clipping by side panels
  // or scroll containers used to swallow long-content tooltips otherwise.
  useLayoutEffect(() => {
    if (!visible) return;
    const node = triggerRef.current;
    if (!node) return;
    setNudge({ dx: 0, dy: 0 });
    setOffset(computeOffset(node.getBoundingClientRect(), position));
  }, [visible, position]);

  // Second pass: once the bubble is laid out at its base position, pull it
  // back inside the viewport so a long/rich tooltip never spills off-screen.
  // Depends only on [visible, offset] — not `nudge` — so it corrects once and
  // does not oscillate.
  useLayoutEffect(() => {
    if (!(visible && offset)) return;
    const bubble = bubbleRef.current;
    if (!bubble || typeof window === 'undefined') return;
    const rect = bubble.getBoundingClientRect();
    let dx = 0;
    let dy = 0;
    if (rect.left < VIEWPORT_MARGIN) {
      dx = VIEWPORT_MARGIN - rect.left;
    } else if (rect.right > window.innerWidth - VIEWPORT_MARGIN) {
      dx = window.innerWidth - VIEWPORT_MARGIN - rect.right;
    }
    if (rect.top < VIEWPORT_MARGIN) {
      dy = VIEWPORT_MARGIN - rect.top;
    } else if (rect.bottom > window.innerHeight - VIEWPORT_MARGIN) {
      dy = window.innerHeight - VIEWPORT_MARGIN - rect.bottom;
    }
    setNudge((prev) => (prev.dx === dx && prev.dy === dy ? prev : { dx, dy }));
  }, [visible, offset]);

  return (
    <div
      className={joinCss(css.root, className)}
      onMouseEnter={disabled ? undefined : show}
      onMouseLeave={disabled ? undefined : requestHide}
      ref={triggerRef}
      role="note"
      {...restProps}
    >
      {children}
      {!disabled &&
        visible &&
        offset &&
        createPortal(
          <div
            className={joinCss(
              css.bubble,
              css[POSITION_CLASS[position]],
              rich && css.bubbleRich,
              bubbleClassName
            )}
            onMouseEnter={rich ? show : undefined}
            onMouseLeave={rich ? requestHide : undefined}
            ref={bubbleRef}
            role="tooltip"
            style={{
              left: offset.left + nudge.dx,
              top: offset.top + nudge.dy,
              transform: offset.transform,
            }}
          >
            <span className={css.text}>{content}</span>
            <span className={css.arrow} />
          </div>,
          document.body
        )}
    </div>
  );
};
